import { useState } from 'react';
import { formatDate } from '../../lib/api';
import type { LicenceRegisterEntry, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** Public licence register — no sign-in required. "Is this business licensed?" for anyone. */
export function LicenceRegister() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [classCode, setClassCode] = useState('');

  const query = new URLSearchParams({ limit: '50' });
  if (q) query.set('q', q);
  if (status) query.set('status', status);
  if (classCode) query.set('classCode', classCode);
  const registerQ = useApiQuery<Page<LicenceRegisterEntry>>(`/api/v1/m14/register?${query.toString()}`);

  return (
    <div>
      <PageHeader title="Licence Register" subtitle="Public search — every SLA licence marked visible on the public register." />
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Serial or holder id…" /></Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="REVOKED">Revoked</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </Field>
        <Field label="Licence class" className="!mb-0"><input className="field-input" value={classCode} onChange={(e) => setClassCode(e.target.value.toUpperCase())} placeholder="e.g. RESTAURANT" /></Field>
      </div>
      {registerQ.error ? <ErrorNote message={registerQ.error} /> : null}
      <div className="card overflow-x-auto">
        {registerQ.loading ? (
          <Spinner />
        ) : (registerQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No licences match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Serial</th>
                <th className="table-head">Class</th>
                <th className="table-head">Holder</th>
                <th className="table-head">Premises</th>
                <th className="table-head">Status</th>
                <th className="table-head">Valid to</th>
              </tr>
            </thead>
            <tbody>
              {registerQ.data!.items.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs font-semibold">{l.serial}</td>
                  <td className="table-cell text-xs">{l.classCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{l.holderType} {l.holderId}</td>
                  <td className="table-cell text-xs">{l.premisesAddress ?? '—'}</td>
                  <td className="table-cell"><StatusBadge status={l.status} /></td>
                  <td className="table-cell text-xs">{formatDate(l.validTo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
